import CONSTANTS from '~/global_helper/constants.js'
import { marked } from 'marked'
import callAnalytics from '~/plugins/stan-analytics.js'
import { configSettings } from '~/plugins/config-settings.js'
import userflow from '~/plugins/userflow.js'
import { getSessionUUID } from '~/store/modules/Global.js'
import { productGenerationTestData } from './test/product-generation-test-data.js'

export const state = {
	isOverlayOpen: false,
	currentTab: 'chat', // 'chat' or 'preview'
	currentChatId: -1,
	generatedPageData: null, // Store product data for navigation
	status: 'Thinking...', // Store status information from status chunks
	chats: [
		{
			id: -1,
			title: 'Store Setup Help',
			loading: false,
			isStreaming: false,
			abortController: null,
			messages: [],
		},
	],
	error: null,
}

export const getters = {
	isChatOverlayOpen: state => state.isOverlayOpen,
	getCurrentTab: state => state.currentTab,
	getCurrentChatId: state => state.currentChatId,
	getCurrentChat: state => state.chats.find(chat => chat.id === state.currentChatId) || state.chats[0],
	getMessages: state => {
		const currentChat = state.chats.find(chat => chat.id === state.currentChatId) || state.chats[0]
		return currentChat ? currentChat.messages : []
	},
	getChatTitle: state => {
		const currentChat = state.chats.find(chat => chat.id === state.currentChatId) || state.chats[0]
		return currentChat ? currentChat.title : 'Chat Assistant'
	},
	getChats: state => state.chats,
	getGeneratedPageData: state => state.generatedPageData,
	isLoading: state => {
		const currentChat = state.chats.find(chat => chat.id === state.currentChatId)
		return currentChat ? currentChat.loading : false
	},
	isStreaming: state => {
		const currentChat = state.chats.find(chat => chat.id === state.currentChatId)
		return currentChat ? currentChat.isStreaming : false
	},
	getStatus: state => state.status,
}

export const mutations = {
	setChatOverlayOpen(state, value) {
		state.isOverlayOpen = value
	},
	setCurrentTab(state, tab) {
		state.currentTab = tab
	},
	setCurrentChatId(state, chatId) {
		state.currentChatId = chatId
	},
	addMessage(state, { chatId, message }) {
		const chat = state.chats.find(chat => String(chat.id) === String(chatId))
		if (chat) {
			chat.messages.push(message)
		}
	},
	setChatLoading(state, { chatId, loading }) {
		const chat = state.chats.find(chat => chat.id === chatId)
		if (chat) {
			chat.loading = loading
		}
	},
	setChatStreaming(state, { chatId, isStreaming }) {
		const chat = state.chats.find(chat => chat.id === chatId)
		if (chat) {
			chat.isStreaming = isStreaming
		}
	},
	setChatAbortController(state, { chatId, controller }) {
		const chat = state.chats.find(chat => chat.id === chatId)
		if (chat) {
			chat.abortController = controller
		}
	},
	setError(state, error) {
		state.error = error
	},
	setChatId(state, { oldChatId, newChatId }) {
		const chat = state.chats.find(chat => String(chat.id) === String(oldChatId))
		if (chat) {
			chat.id = newChatId
		}
	},
	setGeneratedPageData(state, product) {
		state.generatedPageData = product
	},
	setStatus(state, status) {
		state.status = status
	},
	updateMessageContent(state, message) {
		const currentChat = state.chats.find(chat => chat.id === state.currentChatId)
		if (currentChat) {
			const messageToUpdate = currentChat.messages.find(msg => msg.id === message.id)
			if (messageToUpdate) {
				messageToUpdate.content = message.content
				messageToUpdate.content_html = message.content_html
				if (message.products) {
					messageToUpdate.products = message.products
				}
				if (message.instagramMarketingStories) {
					messageToUpdate.instagramMarketingStories = message.instagramMarketingStories
				}
				if (message.attachments) {
					messageToUpdate.attachments = message.attachments
				}
			}
		}
	},

	createNewChat(state) {
		const newChatId = Date.now() * -1
		const newChat = {
			id: newChatId,
			title: 'New Chat',
			loading: false,
			isStreaming: false,
			abortController: null,
			messages: [],
		}
		state.chats.push(newChat)
		state.currentChatId = newChatId
		state.error = null
	},
}

export const actions = {
	openChatOverlay({ commit }) {
		commit('setChatOverlayOpen', true)
	},
	closeChatOverlay({ commit }) {
		commit('setChatOverlayOpen', false)
	},
	setCurrentTab({ commit }, tab) {
		commit('setCurrentTab', tab)
	},
	setCurrentChat({ commit }, chatId) {
		commit('setCurrentChatId', chatId)
	},
	async sendMessage({ commit, state, rootGetters, dispatch }, { messageContent, chatId, attachments = [] }) {
		if (!messageContent.trim() && attachments.length === 0) return

		// temporary test for UI (or permanent, depends)
		if (configSettings.env_name !== 'production') {
			// Check for test trigger first
			const testTriggered = await dispatch('testEddieSandy', { messageContent, chatId })
			if (testTriggered) {
				return // Exit early if test was triggered
			}
		}

		const userId = rootGetters['Auth/user']?.user_id
		const authToken = rootGetters['Auth/token']
		const apiUrl = configSettings.chatAIApiUrlV2
		// const apiUrl = 'http://0.0.0.0:8000'

		// Add user message to chat
		const userMessage = {
			id: Date.now(),
			content: messageContent,
			timestamp: new Date(),
			type: CONSTANTS.CHAT_MESSAGE_TYPES.SENT,
			attachments, // Add attachments to user message
		}

		commit('addMessage', { chatId, message: userMessage })
		commit('setChatLoading', { chatId, loading: true })
		commit('setChatStreaming', { chatId, isStreaming: false })
		commit('setChatAbortController', { chatId, controller: null })

		let finalChatId = chatId

		try {
			const controller = new AbortController()
			const signal = controller.signal
			commit('setChatAbortController', { chatId, controller })

			// Create FormData for multipart/formdata request
			const formData = new FormData()
			formData.append('question', messageContent)
			formData.append('chat_id', chatId < 0 ? '' : chatId.toString())

			// Add files to FormData
			attachments.forEach(file => {
				formData.append('files', file)
			})

			const response = await fetch(`${apiUrl}/api/v2/chat-ai/ask-ndjson`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${authToken}`,
					// Don't set Content-Type for FormData, let browser set it with boundary
				},
				signal,
				body: formData,
			})

			if (!response.ok) {
				throw new Error('Unable to get response')
			}

			// Stream the response
			const reader = response.body.getReader()
			const decoder = new TextDecoder('utf-8')
			let buffer = ''
			let aiMessage = null
			let responseChatId = null
			let responseId = null

			// Stream response content
			// eslint-disable-next-line no-constant-condition
			while (true) {
				// eslint-disable-next-line no-await-in-loop
				const { done, value } = await reader.read()
				if (done) {
					if (aiMessage) {
						commit('setChatStreaming', { chatId: finalChatId, isStreaming: false })
						commit('setChatAbortController', { chatId: finalChatId, controller: null })
					}
					break
				}

				// Decode the chunk and add to buffer
				buffer += decoder.decode(value, { stream: true })

				// Process complete lines (NDJSON format)
				const lines = buffer.split('\n')
				buffer = lines.pop() // Keep incomplete line in buffer

				for (const line of lines) {
					if (line.trim()) {
						try {
							const chunk = JSON.parse(line)

							// Handle marker chunk with metadata
							if (chunk.type === 'marker' && chunk.event === 'RunStarted') {
								responseChatId = chunk.content.chat_id || null
								responseId = chunk.content.response_id || null

								// Update chat ID if backend returns a new one
								if (chatId < 0 && responseChatId) {
									commit('setChatId', { oldChatId: chatId, newChatId: responseChatId })
									finalChatId = responseChatId

									// Update current chat if this was the active chat
									if (state.currentChatId === chatId) {
										commit('setCurrentChatId', responseChatId)
									}
								}

								// Create AI response message
								aiMessage = {
									id: responseId,
									content: '',
									content_html: '',
									timestamp: new Date(),
									type: CONSTANTS.CHAT_MESSAGE_TYPES.RECEIVED,
									feedback_status: null,
									products: [], // Array to store product data
									instagramMarketingStories: [], // Array to store Instagram marketing story data
									instagramMainFeedPosts: [], // Array to store Instagram main feed post data
									attachments: [], // Array to store file attachments
									errors: [], // Array to store any parsing errors
								}

								commit('addMessage', { chatId: finalChatId, message: aiMessage })
								commit('setChatLoading', { chatId: finalChatId, loading: false })
								commit('setChatStreaming', { chatId: finalChatId, isStreaming: true })
								commit('setChatAbortController', { chatId: finalChatId, controller })

								// Track analytics
								callAnalytics('stanley-question-asked', {
									meta: { user_id: userId },
									props: {
										...{ question: messageContent, chat_id: responseChatId, response_id: responseId },
										attachments_count: attachments.length,
									},
								})
								if (messageContent === 'Can you give me some product ideas?') {
									const payload = {
										question: messageContent,
										chat_id: responseChatId,
										response_id: responseId,
										user_id: userId,
										url: window.location.href,
										session_uuid: getSessionUUID(),
									}
									userflow.TrackUserflowEvent('stanley_product_ideation_started', payload)
								}
							}

							// Handle status chunks (update global status)
							if (chunk.type === 'status') {
								commit('setStatus', chunk.content)
							}

							// Update message content based on chunk structure
							if (aiMessage) {
								switch (chunk.type) {
									case 'response':
										if (chunk.content) {
											aiMessage.content += chunk.content
											aiMessage.content_html = marked.parse(aiMessage.content)
										}
										break
									case 'product':
										if (chunk.content) {
											try {
												const productData = chunk.content
												aiMessage.products.push(productData)
											} catch (error) {
												aiMessage.errors.push(error.message)
											}
										}
										break
									case 'instagram-marketing-story':
										if (chunk.content) {
											try {
												const instagramMarketingStoryData = chunk.content
												aiMessage.instagramMarketingStories.push(instagramMarketingStoryData)
											} catch (error) {
												aiMessage.errors.push(error.message)
											}
										}
										break
									case 'instagram-main-feed-post':
										if (chunk.content) {
											aiMessage.instagramMainFeedPosts.push(chunk.content)
										}
										break
									default:
										break
								}
								commit('updateMessageContent', aiMessage)
							}
							// fire events for main feed posts because there are no other places for it
							if (chunk.type === 'main-feed-post') {
								if (chunk.content.type === 'main-feed-post') {
									callAnalytics('chat-v2-marketing-post-main-feed-displayed', {
										meta: {
											user_id: userId,
										},
										props: {
											message_id: aiMessage.id,
											chat_id: finalChatId,
											content_type: chunk.content.type,
										},
									})
									userflow.TrackUserflowEvent('chat-v2-marketing-post-main-feed-displayed', {
										chat_id: finalChatId,
										message_id: aiMessage.id,
										content_type: chunk.content.type,
									})
								}
							}
						} catch (parseError) {
							console.error('Error parsing NDJSON chunk:', parseError, 'Line:', line)
						}
					}
				}
			}
		} catch (e) {
			if (e.name !== 'AbortError') {
				console.error('Error sending message:', e)
			}
		} finally {
			commit('setStatus', 'Thinking...') // reset status
			commit('setChatLoading', { chatId: finalChatId, loading: false })
			commit('setChatStreaming', { chatId: finalChatId, isStreaming: false })
			commit('setChatAbortController', { chatId: finalChatId, controller: null })
		}
	},
	createNewChat({ commit }) {
		commit('createNewChat')
	},
	setGeneratedPageData({ commit }, product) {
		commit('setGeneratedPageData', product)
	},
	abortCurrentRequest({ commit, state }) {
		const currentChat = state.chats.find(chat => chat.id === state.currentChatId)
		if (currentChat && currentChat.abortController) {
			currentChat.abortController.abort()
			commit('setChatAbortController', { chatId: state.currentChatId, controller: null })
		}
	},
	refreshMessage({ dispatch }, { originalQuestion, chatId, attachments = [] }) {
		// Re-send the original question with attachments
		dispatch('sendMessage', {
			messageContent: originalQuestion,
			chatId,
			attachments,
		})
	},

	async handleGetProductIdeas({ commit, state, dispatch }, startNewChat = true) {
		let chatId = state.currentChatId

		// Create new chat for product ideas if requested
		if (startNewChat) {
			commit('createNewChat')
			commit('setChatOverlayOpen', true)
			chatId = state.currentChatId
		}

		// Send the product ideas question
		await dispatch('sendMessage', {
			messageContent: 'Can you give me some product ideas?',
			chatId,
		})
	},

	async handleCreateMarketingPost({ commit, state, dispatch }, startNewChat = true) {
		let chatId = state.currentChatId

		// Create new chat for marketing post if requested
		if (startNewChat) {
			commit('createNewChat')
			commit('setChatOverlayOpen', true)
			chatId = state.currentChatId
		}

		// Send the marketing post question
		await dispatch('sendMessage', {
			messageContent: 'Make me a marketing post',
			chatId,
		})
	},

	// endpoint just for showing UI for testing
	testEddieSandy({ commit, state }, { messageContent }) {
		// Check if the message is the test trigger
		if (messageContent.trim().toLowerCase() === 'open sesame eddie') {
			// Replace chats with test data

			// Replace the chats array
			state.chats.push(productGenerationTestData[0])

			// Set current chat to the first one (E D D I E)
			commit('setCurrentChatId', 1337)

			// Add the user message to the current chat
			const userMessage = {
				id: Date.now(),
				content: messageContent,
				timestamp: new Date(),
				type: CONSTANTS.CHAT_MESSAGE_TYPES.SENT,
			}
			commit('addMessage', { chatId: 1337, message: userMessage })

			return true // Indicate that the test was triggered
		}
		return false // Indicate that the test was not triggered
	},
}
